/**
 * Onay bekleyen siparişler — ödenen sipariş buraya düşer; "Faturala" denince kesilir.
 *
 * Varsayılan elle onay (kullanıcı kararı). Ayarlar → Fatura kuralları → "Siparişler
 * otomatik faturalansın" açıksa bu liste boş kalır; işler doğrudan kuyruğa girer.
 *
 * Liste ekranı şablonu (docs/13): başlık → tablo → eylemler. Kolon değerleri yükten
 * okunur; tutar Shopify'ın `total_price`'ı, KDV dahil ödenen tutardır.
 */

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { RotateCw, Check, SkipForward, TriangleAlert, ExternalLink } from 'lucide-react';
import { api, type FaturaOnizleme, type OnayBekleyenIs } from '../lib/api';
import { Kabuk } from '../components/Kabuk';
import { Kart } from '../components/ui/Kart';
import { Dugme } from '../components/ui/Dugme';
import { Girdi } from '../components/ui/Girdi';
import { Pencere } from '../components/ui/Pencere';
import { DurumRozeti } from '../components/DurumRozeti';
import { HataKutusu } from '../components/Durumlar';
import { VeriTablosu } from '../components/VeriTablosu';
import { TarihHucresi } from '../components/TarihHucresi';
import { paraYaz, tarihYaz } from '../lib/format';
import { basariBildir, bilgiBildir } from '../lib/bildirim';
import { useOturum } from '../oturum';
import { izinVar } from '../lib/yetki';
import { useAdresParametreleri } from '../lib/adres';
import { sayfaBilgisi } from '../lib/sayfa';

/**
 * Sipariş numarası — Shopify yönetim paneline bağlantı (kullanıcı isteği, 10 Eylül 2026:
 * "tıklayınca Shopify'a gidebilsin"). Satır tıklaması pencereyi açar; bağlantı onu tetiklemez.
 */
function SiparisBaglantisi({ is }: { is: OnayBekleyenIs }) {
  const metin = is.siparisNo ?? (is.siparisId ? `#${is.siparisId}` : '—');
  if (!is.siparisAdresi) return <span className="rakam text-sm text-[var(--ink)]">{metin}</span>;
  return (
    <a
      href={is.siparisAdresi}
      target="_blank"
      rel="noreferrer"
      onClick={(o) => o.stopPropagation()}
      className="rakam inline-flex items-center gap-1 text-sm text-[var(--accent)] hover:underline"
      title="Shopify’da aç"
    >
      {metin}
      <ExternalLink size={12} aria-hidden="true" className="-mt-px" />
    </a>
  );
}

/** Onaydan önce belge görünümü — özet kartı + kalem tablosu + toplamlar (detay ekranıyla aynı dil). */
function FaturaOnizlemesi({ o }: { o: FaturaOnizleme }) {
  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-3 text-xs sm:grid-cols-2">
        <div className="flex flex-col gap-1">
          <span className="font-medium text-[var(--ink-muted)]">Belge</span>
          <span className="text-[var(--ink)]">{o.belgeTipi}</span>
          <span className="text-[var(--ink-muted)]">
            Tarih <span className="rakam">{tarihYaz(o.faturaTarihi)}</span> · {o.paraBirimi}
          </span>
        </div>
        <div className="flex flex-col gap-1">
          <span className="font-medium text-[var(--ink-muted)]">Satıcı</span>
          <span className="text-[var(--ink)]">{o.satici.unvan}</span>
          <span className="rakam text-[var(--ink-muted)]">VKN {o.satici.vkn}</span>
        </div>
        <div className="flex flex-col gap-1 sm:col-span-2">
          <span className="font-medium text-[var(--ink-muted)]">Alıcı</span>
          <span className="text-[var(--ink)]">
            {o.alici.unvan} ·{' '}
            <span className="rakam">
              {o.alici.kimlikTipi} {o.alici.kimlik}
            </span>
            {o.alici.vergiDairesi ? ` · ${o.alici.vergiDairesi} VD` : ''}
          </span>
          <span className="text-[var(--ink-muted)]">
            {o.alici.adres.sokak} — {o.alici.adres.ilce} / {o.alici.adres.il}
            {o.alici.adres.postaKodu ? ` ${o.alici.adres.postaKodu}` : ''} · {o.alici.adres.ulke}
          </span>
          {(o.alici.eposta || o.alici.telefon) && (
            <span className="text-[var(--ink-muted)]">
              {[o.alici.eposta, o.alici.telefon].filter(Boolean).join(' · ')}
            </span>
          )}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-[var(--ink-muted)]">
              <th className="py-1 pr-2 font-medium">#</th>
              <th className="py-1 pr-2 font-medium">Ürün</th>
              <th className="py-1 pr-2 text-right font-medium">Adet</th>
              <th className="py-1 pr-2 text-right font-medium">Birim (KDV hariç)</th>
              <th className="py-1 pr-2 text-right font-medium">İskonto</th>
              <th className="py-1 pr-2 text-right font-medium">Tutar</th>
              <th className="py-1 pr-2 text-right font-medium">KDV %</th>
              <th className="py-1 text-right font-medium">KDV</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--line)]">
            {o.satirlar.map((k) => (
              <tr key={k.lineNumber} className="text-[var(--ink)]">
                <td className="rakam py-1 pr-2">{k.lineNumber}</td>
                <td className="py-1 pr-2">{k.name}</td>
                <td className="rakam py-1 pr-2 text-right">{k.quantity}</td>
                <td className="rakam py-1 pr-2 text-right">{paraYaz(k.unitPrice)}</td>
                <td className="rakam py-1 pr-2 text-right">{paraYaz(k.discountAmount)}</td>
                <td className="rakam py-1 pr-2 text-right">{paraYaz(k.lineAmount)}</td>
                <td className="rakam py-1 pr-2 text-right">{k.vatRate}</td>
                <td className="rakam py-1 text-right">{paraYaz(k.vatAmount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <dl className="ml-auto grid w-full max-w-xs grid-cols-2 gap-x-4 gap-y-1 text-xs">
        <dt className="text-[var(--ink-muted)]">Mal / hizmet</dt>
        <dd className="rakam text-right text-[var(--ink)]">{paraYaz(o.toplamlar.malHizmet)}</dd>
        <dt className="text-[var(--ink-muted)]">İskonto</dt>
        <dd className="rakam text-right text-[var(--ink)]">{paraYaz(o.toplamlar.iskonto)}</dd>
        <dt className="text-[var(--ink-muted)]">KDV hariç</dt>
        <dd className="rakam text-right text-[var(--ink)]">{paraYaz(o.toplamlar.kdvHaric)}</dd>
        <dt className="text-[var(--ink-muted)]">KDV</dt>
        <dd className="rakam text-right text-[var(--ink)]">{paraYaz(o.toplamlar.kdv)}</dd>
        <dt className="font-semibold text-[var(--ink)]">Ödenecek</dt>
        <dd className="rakam text-right font-semibold text-[var(--ink)]">
          {paraYaz(o.toplamlar.odenecek)} {o.paraBirimi}
        </dd>
      </dl>

      {o.internetSatis && (
        <p className="text-xs text-[var(--ink-muted)]">
          İnternet satışı: {o.internetSatis.webAdresi} · ödeme {o.internetSatis.odemeSekli}{' '}
          <span className="rakam">{tarihYaz(o.internetSatis.odemeTarihi)}</span>
          {o.internetSatis.kargoFirmasi
            ? ` · kargo ${o.internetSatis.kargoFirmasi} (VKN ${o.internetSatis.kargoVkn ?? '—'})`
            : ''}
          {o.internetSatis.gonderimTarihi ? ` · gönderim ${tarihYaz(o.internetSatis.gonderimTarihi)}` : ''}
        </p>
      )}
      {o.notlar.length > 0 && <p className="text-xs text-[var(--ink-muted)]">Not: {o.notlar.join(' · ')}</p>}
    </div>
  );
}

/** Görünür süzgeç düğmeleri — seçili olan birincil, diğerleri ikincil (el mertebesi). */
function SuzgecSatiri({
  etiket,
  deger,
  secenekler,
  onSec,
}: {
  etiket: string;
  deger: string;
  secenekler: Array<[string, string]>;
  onSec: (deger: string) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="w-16 text-xs font-medium text-[var(--ink-muted)]">{etiket}</span>
      <div className="flex flex-wrap gap-2" role="group" aria-label={etiket}>
        {secenekler.map(([v, ad]) => (
          <Dugme
            key={v}
            boy="sm"
            gorunum={deger === v ? 'birincil' : 'ikincil'}
            aria-pressed={deger === v}
            onClick={() => onSec(v)}
          >
            {ad}
          </Dugme>
        ))}
      </div>
    </div>
  );
}

export function OnayBekleyen() {
  const { kullanici } = useOturum();
  const duzenleyebilir = izinVar(kullanici, 'fatura.duzenle');
  const qc = useQueryClient();
  const [atlanacak, setAtlanacak] = useState<OnayBekleyenIs | null>(null);
  const [acik, setAcik] = useState<OnayBekleyenIs | null>(null);
  const [neden, setNeden] = useState('');
  const { parametreler, adresiGuncelle } = useAdresParametreleri();
  const sayfaNo = Math.max(1, Number(parametreler.get('page') ?? '1') || 1);
  const sayfaBoyu = Number(parametreler.get('pageSize') ?? '25') || 25;
  const eskiler = parametreler.get('eskiler') === 'goster' ? 'goster' : 'gizle';
  const kargoHam = parametreler.get('kargo');
  const kargo: 'hepsi' | 'gonderildi' | 'hazirlaniyor' =
    kargoHam === 'gonderildi' || kargoHam === 'hazirlaniyor' ? kargoHam : 'hepsi';
  const kontrolHam = parametreler.get('kontrol');
  const kontrol: 'hepsi' | 'kesilemez' | 'uyarili' | 'temiz' =
    kontrolHam === 'kesilemez' || kontrolHam === 'uyarili' || kontrolHam === 'temiz' ? kontrolHam : 'hepsi';
  const [topluAtlaAcik, setTopluAtlaAcik] = useState(false);

  const { data, error, isPending, isFetching, refetch } = useQuery({
    queryKey: ['onay-bekleyen', { sayfaNo, sayfaBoyu, eskiler, kontrol, kargo }],
    queryFn: () => api.onayBekleyenler({ page: sayfaNo, pageSize: sayfaBoyu, eskiler, kontrol, kargo }),
    refetchInterval: 20_000,
    placeholderData: (onceki) => onceki,
  });
  // Alt listeler: kendi sayfa parametreleri (bpage/spage) — üç tablo birbirinden bağımsız sayfalanır.
  const bSayfaNo = Math.max(1, Number(parametreler.get('bpage') ?? '1') || 1);
  const bSayfaBoyu = Number(parametreler.get('bpageSize') ?? '10') || 10;
  const sSayfaNo = Math.max(1, Number(parametreler.get('spage') ?? '1') || 1);
  const sSayfaBoyu = Number(parametreler.get('spageSize') ?? '10') || 10;
  const basarisiz = useQuery({
    queryKey: ['onay-bekleyen', 'DEAD', { bSayfaNo, bSayfaBoyu }],
    queryFn: () => api.onayListesi('DEAD', { page: bSayfaNo, pageSize: bSayfaBoyu }),
    enabled: (data?.basarisizSayisi ?? 0) > 0,
    refetchInterval: 20_000,
    placeholderData: (onceki) => onceki,
  });
  const sirada = useQuery({
    queryKey: ['onay-bekleyen', 'QUEUED', { sSayfaNo, sSayfaBoyu }],
    queryFn: () => api.onayListesi('QUEUED', { page: sSayfaNo, pageSize: sSayfaBoyu }),
    enabled: (data?.siradaSayisi ?? 0) > 0,
    refetchInterval: 20_000,
    placeholderData: (onceki) => onceki,
  });

  const yenile = () => void qc.invalidateQueries({ queryKey: ['onay-bekleyen'] });
  const faturala = useMutation({
    mutationFn: (id: string) => api.onayFaturala(id),
    onSuccess: (sonuc) => {
      if (sonuc.kurulumEksik) {
        bilgiBildir(
          `Sipariş kuyruğa alındı ama KURULUM TAMAMLANMADI; kurulum bitince otomatik kesilecek. Eksik: ${sonuc.engeller.join(' · ')}`,
          'Fatura henüz kesilmeyecek'
        );
      } else {
        basariBildir('Sipariş kuyruğa alındı; fatura birkaç saniye içinde Faturalar listesinde görünür.');
      }
      yenile();
    },
  });
  const atla = useMutation({
    mutationFn: ({ id, neden }: { id: string; neden: string }) => api.onayAtla(id, neden),
    onSuccess: () => {
      setAtlanacak(null);
      setNeden('');
      yenile();
    },
  });
  const yenidenAl = useMutation({
    mutationFn: (id: string) => api.onayYenidenAl(id),
    onSuccess: () => {
      bilgiBildir(
        'Sipariş yeniden onay listesine alındı; sebebi düzelttiyseniz "Faturala" deyin.',
        'Onaya alındı'
      );
      yenile();
    },
  });
  const topluAtla = useMutation({
    mutationFn: () => api.onayBaslangicOncesiAtla(),
    onSuccess: (sonuc) => {
      setTopluAtlaAcik(false);
      basariBildir(`${sonuc.atlanan} sipariş "başlangıç tarihi öncesi" notuyla atlandı; fatura kesilmedi.`);
      yenile();
    },
  });
  // Adres düzeltme formu — pencere açılan siparişe göre doldurulur.
  const BOS_ADRES = { address1: '', address2: '', il: '', ilce: '', zip: '' };
  const [adresForm, setAdresForm] = useState(BOS_ADRES);
  useEffect(() => {
    if (acik?.adres) {
      const a = acik.adres;
      setAdresForm({ address1: a.address1, address2: a.address2, il: a.il, ilce: a.ilce, zip: a.zip });
    } else {
      setAdresForm(BOS_ADRES);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- yalnız açılan iş değişince
  }, [acik?.id]);
  // Fatura önizlemesi — pencere açıkken, engelsiz satış işleri için kuru koşu.
  const onizleme = useQuery({
    queryKey: ['onay-onizleme', acik?.id ?? null],
    queryFn: () => api.onayOnizleme(acik!.id),
    enabled: acik !== null && acik.tur === 'SATIS' && acik.engel === null,
    staleTime: 15_000,
  });
  const adresKaydet = useMutation({
    mutationFn: ({ id, adres }: { id: string; adres: typeof BOS_ADRES }) => api.onayAdresGuncelle(id, adres),
    onSuccess: (sonuc) => {
      setAcik(null);
      if (sonuc.engel)
        bilgiBildir(`Adres Shopify’a yazıldı ama hâlâ kesilemez: ${sonuc.engel}`, 'Engel sürüyor');
      else if (sonuc.uyarilar.length > 0)
        bilgiBildir(`Adres Shopify’a yazıldı; ${sonuc.uyarilar.length} uyarı kaldı.`, 'Güncellendi');
      else basariBildir('Adres Shopify’a yazıldı; sipariş temiz, faturalanabilir.');
      yenile();
    },
  });
  const yenileShopify = useMutation({
    mutationFn: (id: string) => api.onayYenile(id),
    onSuccess: (sonuc) => {
      setAcik(null);
      if (sonuc.engel) {
        bilgiBildir(`Shopify’dan yenilendi ama hâlâ kesilemez: ${sonuc.engel}`, 'Engel sürüyor');
      } else if (sonuc.uyarilar.length > 0) {
        bilgiBildir(`Shopify’dan yenilendi; ${sonuc.uyarilar.length} uyarı kaldı.`, 'Yenilendi');
      } else {
        basariBildir('Shopify’dan yenilendi; sipariş temiz, faturalanabilir.');
      }
      yenile();
    },
  });
  const geriAl = useMutation({
    mutationFn: (id: string) => api.onayGeriAl(id),
    onSuccess: () => {
      bilgiBildir('İş onaya geri alındı; gönderilmeyecek.', 'Geri alındı');
      yenile();
    },
  });

  const kolonlar = useMemo<ColumnDef<OnayBekleyenIs, unknown>[]>(
    () => [
      {
        id: 'geldi',
        header: 'Geldi',
        enableSorting: false,
        cell: ({ row }) => <TarihHucresi zaman={row.original.geldi} />,
      },
      {
        id: 'tur',
        header: 'Tür',
        enableSorting: false,
        cell: ({ row }) => (
          <DurumRozeti
            ton={row.original.tur === 'IADE' ? 'info' : 'neutral'}
            etiket={row.original.tur === 'IADE' ? 'İade' : 'Satış'}
          />
        ),
      },
      {
        id: 'siparis',
        header: 'Sipariş',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex min-w-0 flex-col">
            <SiparisBaglantisi is={row.original} />
            <span className="text-xs text-[var(--ink-muted)]">
              {row.original.kaynak === 'mutabakat' ? 'Mutabakat buldu' : 'Shopify bildirimi'}
            </span>
          </div>
        ),
      },
      {
        id: 'gonderim',
        header: 'Kargo',
        enableSorting: false,
        cell: ({ row }) => {
          const g = row.original.gonderim;
          if (!g) return <span className="text-xs text-[var(--ink-faint)]">—</span>;
          const rozet =
            g.durum === 'GONDERILDI'
              ? { ton: 'success' as const, etiket: 'Gönderildi' }
              : g.durum === 'KISMEN'
                ? { ton: 'warning' as const, etiket: 'Kısmen' }
                : g.durum === 'BEKLIYOR'
                  ? { ton: 'neutral' as const, etiket: 'Hazırlanıyor' }
                  : { ton: 'neutral' as const, etiket: 'Bilinmiyor' };
          return (
            <div className="flex min-w-0 flex-col gap-1">
              <DurumRozeti ton={rozet.ton} etiket={rozet.etiket} />
              {(g.kargo || g.tarih) && (
                <span className="text-xs text-[var(--ink-muted)]">
                  {[g.kargo, g.tarih ? tarihYaz(g.tarih) : null].filter(Boolean).join(' · ')}
                </span>
              )}
            </div>
          );
        },
      },
      {
        id: 'kontrol',
        header: 'Kontrol',
        enableSorting: false,
        cell: ({ row }) =>
          row.original.engel ? (
            <div className="flex min-w-0 flex-col gap-1">
              <DurumRozeti ton="danger" etiket="Kesilemez" />
              <span className="line-clamp-2 max-w-[36ch] text-xs text-[var(--state-bad)]">
                {row.original.engel}
              </span>
            </div>
          ) : row.original.uyarilar.length > 0 ? (
            <div className="flex min-w-0 flex-col gap-1">
              <DurumRozeti ton="warning" etiket={`${row.original.uyarilar.length} uyarı`} />
              <span className="line-clamp-2 max-w-[36ch] text-xs text-[var(--ink-muted)]">
                {row.original.uyarilar[0]}
              </span>
            </div>
          ) : (
            <DurumRozeti ton="success" etiket="Temiz" />
          ),
      },
      {
        id: 'musteri',
        header: 'Müşteri',
        enableSorting: false,
        cell: ({ row }) => (
          <span className="truncate text-sm text-[var(--ink)]">{row.original.musteri ?? '—'}</span>
        ),
      },
      {
        id: 'tutar',
        header: 'Tutar',
        enableSorting: false,
        cell: ({ row }) => (
          <span className="rakam text-sm text-[var(--ink)]">
            {row.original.tutar ? `${paraYaz(row.original.tutar)} ${row.original.paraBirimi ?? ''}` : '—'}
          </span>
        ),
      },
      {
        id: 'eylem',
        header: '',
        enableSorting: false,
        cell: ({ row }) =>
          duzenleyebilir ? (
            <div className="flex justify-end gap-2" onClick={(o) => o.stopPropagation()}>
              <Dugme
                gorunum="birincil"
                boy="sm"
                ikon={<Check size={14} />}
                bekliyor={faturala.isPending && faturala.variables === row.original.id}
                onClick={() => faturala.mutate(row.original.id)}
                disabled={row.original.engel !== null}
                title={row.original.engel ?? undefined}
              >
                Faturala
              </Dugme>
              <Dugme
                gorunum="ikincil"
                boy="sm"
                ikon={<SkipForward size={14} />}
                onClick={() => setAtlanacak(row.original)}
              >
                Atla
              </Dugme>
            </div>
          ) : null,
      },
    ],
    [duzenleyebilir, faturala]
  );

  type AltIs = OnayBekleyenIs & { hata: string; zaman: string };
  const turKolonu: ColumnDef<AltIs, unknown> = {
    id: 'tur',
    header: 'Tür',
    enableSorting: false,
    cell: ({ row }) => (
      <DurumRozeti
        ton={row.original.tur === 'IADE' ? 'info' : 'neutral'}
        etiket={row.original.tur === 'IADE' ? 'İade' : 'Satış'}
      />
    ),
  };
  const siparisKolonu: ColumnDef<AltIs, unknown> = {
    id: 'siparis',
    header: 'Sipariş',
    enableSorting: false,
    cell: ({ row }) => <SiparisBaglantisi is={row.original} />,
  };
  const tutarKolonu: ColumnDef<AltIs, unknown> = {
    id: 'tutar',
    header: 'Tutar',
    enableSorting: false,
    cell: ({ row }) => (
      <span className="rakam text-sm text-[var(--ink)]">
        {row.original.tutar ? `${paraYaz(row.original.tutar)} ${row.original.paraBirimi ?? ''}` : '—'}
      </span>
    ),
  };
  const basarisizKolonlar = useMemo<ColumnDef<AltIs, unknown>[]>(
    () => [
      {
        id: 'zaman',
        header: 'Düştü',
        enableSorting: false,
        cell: ({ row }) => <TarihHucresi zaman={row.original.zaman} />,
      },
      turKolonu,
      siparisKolonu,
      {
        id: 'hata',
        header: 'Sebep',
        enableSorting: false,
        cell: ({ row }) => (
          <span className="line-clamp-3 max-w-[60ch] text-xs text-[var(--state-bad)]">
            {row.original.hata}
          </span>
        ),
      },
      tutarKolonu,
      {
        id: 'eylem',
        header: '',
        enableSorting: false,
        cell: ({ row }) =>
          duzenleyebilir ? (
            <div className="flex justify-end gap-2">
              <Dugme
                gorunum="ikincil"
                boy="sm"
                bekliyor={yenidenAl.isPending && yenidenAl.variables === row.original.id}
                onClick={() => yenidenAl.mutate(row.original.id)}
              >
                Onaya al
              </Dugme>
              <Dugme gorunum="sessiz" boy="sm" onClick={() => setAtlanacak(row.original)}>
                Atla
              </Dugme>
            </div>
          ) : null,
      },
    ],
    [duzenleyebilir, yenidenAl]
  );
  const siradaKolonlar = useMemo<ColumnDef<AltIs, unknown>[]>(
    () => [
      {
        id: 'geldi',
        header: 'Geldi',
        enableSorting: false,
        cell: ({ row }) => <TarihHucresi zaman={row.original.geldi} />,
      },
      turKolonu,
      siparisKolonu,
      tutarKolonu,
      {
        id: 'eylem',
        header: '',
        enableSorting: false,
        cell: ({ row }) =>
          duzenleyebilir ? (
            <div className="flex justify-end">
              <Dugme
                gorunum="ikincil"
                boy="sm"
                bekliyor={geriAl.isPending && geriAl.variables === row.original.id}
                onClick={() => geriAl.mutate(row.original.id)}
              >
                Geri al
              </Dugme>
            </div>
          ) : null,
      },
    ],
    [duzenleyebilir, geriAl]
  );

  const adet = data?.total ?? 0;

  return (
    <Kabuk
      baslik="Onay bekleyen siparişler"
      kirintilar={[{ etiket: 'Günlük iş' }, { etiket: 'Onay bekleyen' }]}
      altBaslik={
        adet > 0
          ? `${adet} sipariş faturalanmak için onay bekliyor`
          : 'Ödenen siparişler burada toplanır; "Faturala" denince kesilir'
      }
      eylemler={
        <div className="flex gap-2">
          <Dugme
            gorunum="ikincil"
            ikon={<RotateCw size={15} />}
            bekliyor={isFetching && !isPending}
            onClick={() => void refetch()}
          >
            Yenile
          </Dugme>
        </div>
      }
    >
      <div className="kap uzuv p-6 text-sm text-[var(--ink-muted)]">
        Otomatik kesim istiyorsanız{' '}
        <Link to="/ayarlar" className="text-[var(--accent)] hover:underline">
          Ayarlar → Fatura kuralları → &quot;Siparişler otomatik faturalansın&quot;
        </Link>{' '}
        anahtarını açın. Onaylanan sipariş kuyruğa girer; kurulum ve ön kontrol kapılarından geçmezse Hata
        kayıtlarında görünür.
      </div>

      {data && !data.kurulum.faturaKesilebilir && (
        <div
          role="alert"
          className="kap uzuv flex gap-3 border-l-4 border-[var(--state-wait)] bg-[var(--state-wait-soft)] p-6"
        >
          <TriangleAlert size={20} className="mt-0.5 shrink-0 text-[var(--state-wait)]" />
          <div className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-[var(--ink)]">
              Kurulum tamamlanmadı — onayladığınız siparişler kuyrukta bekler, fatura kesilmez.
            </span>
            <span className="text-[var(--ink-muted)]">Eksik: {data.kurulum.engeller.join(' · ')}</span>
            <Link to="/kurulum" className="text-[var(--accent)] hover:underline">
              Kurulum ekranına git
            </Link>
          </div>
        </div>
      )}

      {data && data.baslangicOncesi > 0 && (
        <div
          role="status"
          className="kap uzuv flex flex-wrap items-center justify-between gap-3 border-l-4 border-[var(--line)] bg-[var(--surface)] p-6"
        >
          <div className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-[var(--ink)]">
              <span className="rakam">{data.baslangicOncesi}</span> sipariş fatura başlangıç tarihinden (
              <span className="rakam">{data.baslangicTarihi}</span>) önce —{' '}
              {data.eskilerGosteriliyor ? 'listede gösteriliyor' : 'listede gizli'}.
            </span>
            <span className="text-[var(--ink-muted)]">
              Bu siparişlere bu sistem fatura kesmez. Listeden düşürmek için &quot;Hepsini atla&quot;;
              kesilmeleri gerekiyorsa Ayarlar → Fatura kuralları → başlangıç tarihini geri alın.
            </span>
          </div>
          <div className="flex gap-2">
            <Dugme
              gorunum="ikincil"
              boy="sm"
              onClick={() => adresiGuncelle({ eskiler: data.eskilerGosteriliyor ? undefined : 'goster' })}
            >
              {data.eskilerGosteriliyor ? 'Gizle' : 'Göster'}
            </Dugme>
            {duzenleyebilir && (
              <Dugme
                gorunum="ikincil"
                boy="sm"
                ikon={<SkipForward size={14} />}
                onClick={() => setTopluAtlaAcik(true)}
              >
                Hepsini atla
              </Dugme>
            )}
          </div>
        </div>
      )}

      {topluAtlaAcik && data && (
        <Pencere
          acik
          baslik={`${data.baslangicOncesi} siparişi atla`}
          aciklama="Fatura KESİLMEZ; işler 'başlangıç tarihi öncesi' notuyla kapanır ve denetim izine yazılır."
          onKapat={() => setTopluAtlaAcik(false)}
          altCubuk={
            <>
              <Dugme gorunum="ikincil" onClick={() => setTopluAtlaAcik(false)}>
                Vazgeç
              </Dugme>
              <Dugme gorunum="birincil" bekliyor={topluAtla.isPending} onClick={() => topluAtla.mutate()}>
                Evet, hepsini atla
              </Dugme>
            </>
          }
        >
          <p className="text-sm text-[var(--ink)]">
            Başlangıç tarihi <span className="rakam">{data.baslangicTarihi}</span> öncesindeki{' '}
            <span className="rakam">{data.baslangicOncesi}</span> sipariş onay listesinden düşer. Bu
            siparişlere bu sistemden fatura kesilmeyecek; başka yolla kesilmediyse mali müşavirinizle görüşün.
          </p>
        </Pencere>
      )}

      {topluAtla.error !== null && <HataKutusu hata={topluAtla.error} tekrarDene={yenile} />}

      {data?.hazirlik && !data.hazirlik.hazir && (
        <div
          role="alert"
          className="kap uzuv flex gap-3 border-l-4 border-[var(--state-bad)] bg-[var(--state-bad-soft)] p-6"
        >
          <TriangleAlert size={20} className="mt-0.5 shrink-0 text-[var(--state-bad)]" />
          <div className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-[var(--ink)]">
              Fatura kapısı KAPALI — onaylanan işler sırada bekler, hiçbiri gönderilmez.
            </span>
            {data.hazirlik.kontroller
              .filter((k) => k.durum !== 'TAMAM')
              .map((k) => (
                <span key={k.ad} className="text-[var(--ink-muted)]">
                  {k.mesaj}
                  {k.yapilacak ? ` — ${k.yapilacak}` : ''}
                </span>
              ))}
          </div>
        </div>
      )}

      {error !== null && <HataKutusu hata={error} tekrarDene={() => void refetch()} />}
      {faturala.error !== null && <HataKutusu hata={faturala.error} tekrarDene={yenile} />}
      {geriAl.error !== null && <HataKutusu hata={geriAl.error} tekrarDene={yenile} />}
      {yenileShopify.error !== null && <HataKutusu hata={yenileShopify.error} tekrarDene={yenile} />}
      {yenidenAl.error !== null && <HataKutusu hata={yenidenAl.error} tekrarDene={yenile} />}

      <Kart>
        {/* Süzgeçler — hepsi görünür düğme (açılır kutu değil), sunucu tarafında uygulanır, adres parametresiyle paylaşılır. */}
        <div className="flex flex-col gap-3 p-6">
          <SuzgecSatiri
            etiket="Kontrol"
            deger={kontrol}
            secenekler={[
              ['hepsi', 'Hepsi'],
              ['kesilemez', 'Kesilemez'],
              ['uyarili', 'Uyarılı'],
              ['temiz', 'Temiz'],
            ]}
            onSec={(v) => adresiGuncelle({ kontrol: v === 'hepsi' ? undefined : v })}
          />
          <SuzgecSatiri
            etiket="Kargo"
            deger={kargo}
            secenekler={[
              ['hepsi', 'Hepsi'],
              ['gonderildi', 'Gönderildi'],
              ['hazirlaniyor', 'Hazırlanıyor'],
            ]}
            onSec={(v) => adresiGuncelle({ kargo: v === 'hepsi' ? undefined : v })}
          />
        </div>
        <VeriTablosu
          veri={data?.items ?? []}
          kolonlar={kolonlar}
          sayfa={sayfaBilgisi(data, sayfaNo, sayfaBoyu)}
          siralama={[]}
          yukleniyor={isPending}
          tazeleniyor={isFetching && !isPending}
          satirAnahtari={(s) => s.id}
          onSiralama={() => undefined}
          onSayfa={(yeni) => adresiGuncelle({ page: yeni > 1 ? yeni : undefined }, false)}
          onSayfaBoyu={(boy) => adresiGuncelle({ pageSize: boy === 25 ? undefined : boy })}
          onSatirTikla={(s) => setAcik(s)}
          bosBaslik="Onay bekleyen sipariş yok"
          bosAciklama="Yeni bir sipariş ödendiğinde burada görünür."
        />
      </Kart>

      {data && data.basarisizSayisi > 0 && (
        <Kart className="border border-[var(--state-bad)]">
          <div className="flex flex-col gap-3 p-6 pb-0">
            <h2 className="text-sm font-semibold text-[var(--state-bad)]">
              Kesilemedi ({data.basarisizSayisi}) — onaylandı ama fatura oluşmadı
            </h2>
            <p className="max-w-[65ch] text-sm text-[var(--ink-muted)]">
              Sebep her satırda yazıyor. Sebep Shopify tarafındaysa (vergi ayarı, eksik adres) orada düzeltip
              &quot;Onaya al&quot; deyin; sipariş yeniden onay listesine düşer. Faturalanmayacaksa
              &quot;Atla&quot;.
            </p>
          </div>
          {basarisiz.error !== null && <HataKutusu hata={basarisiz.error} tekrarDene={yenile} />}
          <VeriTablosu
            veri={basarisiz.data?.items ?? []}
            kolonlar={basarisizKolonlar}
            sayfa={sayfaBilgisi(basarisiz.data, bSayfaNo, bSayfaBoyu)}
            siralama={[]}
            yukleniyor={basarisiz.isPending}
            tazeleniyor={basarisiz.isFetching && !basarisiz.isPending}
            satirAnahtari={(s) => s.id}
            onSiralama={() => undefined}
            onSayfa={(yeni) => adresiGuncelle({ bpage: yeni > 1 ? yeni : undefined }, false)}
            onSayfaBoyu={(boy) =>
              adresiGuncelle({ bpageSize: boy === 10 ? undefined : boy, bpage: undefined }, false)
            }
            bosBaslik="Kesilemeyen iş yok"
            bosAciklama="Onaylanan her iş kesildi ya da sırada."
          />
        </Kart>
      )}

      {data && data.siradaSayisi > 0 && (
        <Kart>
          <div className="flex flex-col gap-3 p-6 pb-0">
            <h2 className="text-sm font-semibold text-[var(--ink)]">
              Onaylandı, sırada bekliyor ({data.siradaSayisi})
            </h2>
            <p className="max-w-[65ch] text-sm text-[var(--ink-muted)]">
              Bu işler onaylandı ama henüz gönderilmedi (kapı kapalı ya da sırasını bekliyor). &quot;Geri
              al&quot; işi onaya döndürür; gönderilmez.
            </p>
          </div>
          {sirada.error !== null && <HataKutusu hata={sirada.error} tekrarDene={yenile} />}
          <VeriTablosu
            veri={sirada.data?.items ?? []}
            kolonlar={siradaKolonlar}
            sayfa={sayfaBilgisi(sirada.data, sSayfaNo, sSayfaBoyu)}
            siralama={[]}
            yukleniyor={sirada.isPending}
            tazeleniyor={sirada.isFetching && !sirada.isPending}
            satirAnahtari={(s) => s.id}
            onSiralama={() => undefined}
            onSayfa={(yeni) => adresiGuncelle({ spage: yeni > 1 ? yeni : undefined }, false)}
            onSayfaBoyu={(boy) =>
              adresiGuncelle({ spageSize: boy === 10 ? undefined : boy, spage: undefined }, false)
            }
            bosBaslik="Sırada iş yok"
            bosAciklama="Onaylanan işler gönderildi."
          />
        </Kart>
      )}

      {acik && (
        <Pencere
          acik
          genis
          baslik={`${acik.siparisNo ?? acik.siparisId ?? 'Sipariş'} — onay öncesi kontrol`}
          {...(acik.tutar ? { aciklama: `${paraYaz(acik.tutar)} ${acik.paraBirimi ?? ''}` } : {})}
          onKapat={() => setAcik(null)}
          altCubuk={
            <>
              {duzenleyebilir && acik.tur === 'SATIS' && (
                <Dugme
                  gorunum="ikincil"
                  ikon={<RotateCw size={14} />}
                  bekliyor={yenileShopify.isPending}
                  onClick={() => yenileShopify.mutate(acik.id)}
                  title="Adres/vergi Shopify’da düzeltildiyse siparişi yeniden çeker ve kontrolü tekrarlar"
                >
                  Shopify’dan yenile
                </Dugme>
              )}
              <Dugme gorunum="ikincil" onClick={() => setAcik(null)}>
                Kapat
              </Dugme>
            </>
          }
        >
          <div className="flex flex-col gap-3 text-sm">
            {acik.tur === 'SATIS' && (acik.engel || acik.uyarilar.length > 0) && (
              <p className="text-xs text-[var(--ink-muted)]">
                Düzeltme Shopify’da yapılır (sipariş → adres / vergi). Düzelttikten sonra &quot;Shopify’dan
                yenile&quot; ile buradaki kontrol tekrarlanır.
              </p>
            )}
            {acik.engel ? (
              <p className="text-[var(--state-bad)]">
                <strong>Kesilemez:</strong> {acik.engel}
              </p>
            ) : acik.uyarilar.length > 0 ? (
              <>
                <p className="text-[var(--ink-muted)]">
                  Fatura kesilebilir; ama aşağıdakiler tahminle dolduruldu ya da Shopify ayarından
                  kaynaklanıyor. Uygun değilse Shopify’da düzeltip siparişi yeniden getirin ya da
                  &quot;Atla&quot;.
                </p>
                <ul className="flex list-disc flex-col gap-1 pl-5 text-[var(--ink)]">
                  {acik.uyarilar.map((u) => (
                    <li key={u}>{u}</li>
                  ))}
                </ul>
              </>
            ) : (
              <p className="text-[var(--state-ok)]">
                Ön kontrol ve eşleme temiz — fatura sorunsuz kesilebilir.
              </p>
            )}

            {acik.tur === 'SATIS' && acik.adres && (
              <div className="flex flex-col gap-3 border-t border-[var(--line)] pt-3">
                <div className="flex flex-col gap-1">
                  <h3 className="text-sm font-semibold text-[var(--ink)]">Adres</h3>
                  <p className="text-xs text-[var(--ink-muted)]">
                    Faturaya gidecek adres (
                    {acik.adres.kaynak === 'billing' ? 'Shopify fatura adresi' : 'Shopify teslimat adresi'}).
                    Düzeltip kaydedince Shopify’daki <strong>teslimat adresi</strong> güncellenir (Shopify
                    fatura adresini API ile değiştirmez); fatura, ilçesi çözülen adresi kullanır. İl ve ilçe
                    ayrı yazılmalı.
                  </p>
                  {acik.adres.not && <p className="text-xs text-[var(--state-wait)]">{acik.adres.not}</p>}
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Girdi
                    etiket="İl"
                    value={adresForm.il}
                    onChange={(o) => setAdresForm({ ...adresForm, il: o.target.value })}
                    placeholder="İstanbul"
                  />
                  <Girdi
                    etiket="İlçe"
                    value={adresForm.ilce}
                    onChange={(o) => setAdresForm({ ...adresForm, ilce: o.target.value })}
                    placeholder="Kadıköy"
                  />
                </div>
                <Girdi
                  etiket="Adres satırı 1"
                  value={adresForm.address1}
                  onChange={(o) => setAdresForm({ ...adresForm, address1: o.target.value })}
                />
                <div className="grid gap-3 sm:grid-cols-2">
                  <Girdi
                    etiket="Adres satırı 2 (isteğe bağlı)"
                    value={adresForm.address2}
                    onChange={(o) => setAdresForm({ ...adresForm, address2: o.target.value })}
                  />
                  <Girdi
                    etiket="Posta kodu (5 hane ya da boş)"
                    value={adresForm.zip}
                    onChange={(o) => setAdresForm({ ...adresForm, zip: o.target.value })}
                    className="rakam"
                  />
                </div>
                {duzenleyebilir && (
                  <div className="flex justify-end">
                    <Dugme
                      gorunum="birincil"
                      boy="sm"
                      bekliyor={adresKaydet.isPending}
                      onClick={() => adresKaydet.mutate({ id: acik.id, adres: adresForm })}
                    >
                      Shopify’a kaydet ve yeniden kontrol et
                    </Dugme>
                  </div>
                )}
                {adresKaydet.error !== null && (
                  <HataKutusu hata={adresKaydet.error} tekrarDene={() => adresKaydet.reset()} />
                )}
              </div>
            )}

            {acik.tur === 'SATIS' && acik.engel === null && (
              <div className="flex flex-col gap-3 border-t border-[var(--line)] pt-3">
                <h3 className="text-sm font-semibold text-[var(--ink)]">Faturada nasıl görünecek</h3>
                {onizleme.isPending && (
                  <p className="text-xs text-[var(--ink-muted)]">Önizleme hazırlanıyor…</p>
                )}
                {onizleme.error !== null && (
                  <HataKutusu hata={onizleme.error} tekrarDene={() => void onizleme.refetch()} />
                )}
                {onizleme.data && <FaturaOnizlemesi o={onizleme.data} />}
              </div>
            )}
          </div>
        </Pencere>
      )}

      {atlanacak && (
        <Pencere
          acik
          baslik="Siparişi faturalamadan atla"
          aciklama={atlanacak.siparisNo ?? atlanacak.siparisId ?? ''}
          onKapat={() => setAtlanacak(null)}
          altCubuk={
            <>
              <Dugme gorunum="ikincil" onClick={() => setAtlanacak(null)}>
                Vazgeç
              </Dugme>
              <Dugme
                gorunum="tehlike"
                bekliyor={atla.isPending}
                onClick={() => atla.mutate({ id: atlanacak.id, neden })}
              >
                Atla
              </Dugme>
            </>
          }
        >
          <div className="flex flex-col gap-3">
            <p className="text-sm text-[var(--ink-muted)]">
              Bu sipariş için fatura kesilmez. Kayıt işlem kaydına düşer; gerekirse mutabakat ekranından ya da
              Shopify bildirimini yeniden tetikleyerek tekrar getirilebilir.
            </p>
            <Girdi etiket="Neden (isteğe bağlı)" value={neden} onChange={(o) => setNeden(o.target.value)} />
          </div>
        </Pencere>
      )}
    </Kabuk>
  );
}
